/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import {PageContext, Component} from '@salesforce/commerce-sdk-react/components'
import {PDCDisclaimerBlock} from '../../page-designer/assets/pdc-disclaimer-block'
import {getPageDesignerRegion} from '../../utils/utils'

const PAGEDESIGNER_TO_COMPONENT = {
    'commerce_assets.pdcDisclaimerBlock': PDCDisclaimerBlock
}

/**
 * DisclaimerBlock is a React functional component that renders a set of disclaimer components
 * within a specified region of a page. It utilizes the Page Designer framework to dynamically
 * fetch and render components based on the provided page and region.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.page - The page object containing the data and structure for rendering.
 * @param {string} [props.region='disclaimer'] - The region within the page from which to fetch
 * the disclaimer components. Defaults to 'disclaimer'.
 * @param {boolean} [props.textCenter] - A flag indicating whether the text within the components
 * should be centered.
 * @param {string} [props.padding] - The padding to be applied to the components.
 * @param {Object} props.props - Additional properties to be spread onto the Box component.
 *
 * @returns {JSX.Element|null} A JSX element containing the rendered disclaimer components
 * within a Box, or null if no components are found for the specified region.
 */
const DisclaimerBlock = ({page, region = 'disclaimer', textCenter, padding, ...props}) => {
    const disclaimers = getPageDesignerRegion(page, region)?.components
    const [contextValue] = useState({components: PAGEDESIGNER_TO_COMPONENT})
    return disclaimers?.length ? (
        <Box {...props}>
            {disclaimers?.map((component) => {
                component.textCenter = textCenter
                component.padding = padding
                return (
                    <PageContext.Provider key={component?.id} value={contextValue}>
                        <Component component={component} />
                    </PageContext.Provider>
                )
            })}
        </Box>
    ) : null
}

DisclaimerBlock.propTypes = {
    page: PropTypes.object,
    region: PropTypes.string,
    textCenter: PropTypes.string,
    padding: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.number])
}

export default DisclaimerBlock
